import React, { useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter, Eye } from 'lucide-react';
import InteractiveMap from '../../components/Map/InteractiveMap';
import { identityService, licenseService } from '../../services/api';
import Can from '../Common/Can';
import { P } from '../../constants/permissions';
import { useApiQuery } from '../../hooks/useApi';
import { mergeLicenseStatusForEnterprises } from '../../utils/enterpriseLicenseMap';
import { fetchAllPages } from '../../utils/apiPagination';

const MapCard = ({ title, icon: Icon }) => {
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ status: '', type: '', region: '' });
  const { data: entPayload, isLoading: entLoading } = useApiQuery('map-page-enterprises', () =>
    fetchAllPages((params) => identityService.getEnterprises(params))
  );
  const { data: licPayload, isLoading: licLoading } = useApiQuery('map-page-licencas', () =>
    fetchAllPages((params) => licenseService.getLicencas(params))
  );

  const isLoading = entLoading || licLoading;

  const enterprises = useMemo(() => {
    const raw = Array.isArray(entPayload) ? entPayload : entPayload?.data ?? entPayload?.items ?? [];
    const licRaw = Array.isArray(licPayload) ? licPayload : licPayload?.data ?? licPayload?.items ?? [];
    const healthByEmp = mergeLicenseStatusForEnterprises(licRaw || []);

    const list = (raw || [])
      .filter((e) => e.latitude != null && e.longitude != null)
      .map((enterprise) => {
        const typeRaw = (enterprise.type || 'default').toString();
        const typeLower = typeRaw.toLowerCase();
        return {
          id: enterprise.id,
          name: enterprise.name || enterprise.nome,
          type: typeLower,
          is_active: enterprise.status === 'ACTIVE' || enterprise.is_active !== false,
          description: enterprise.description,
          address: enterprise.address,
          city: enterprise.city,
          state: enterprise.state,
          latitude: enterprise.latitude,
          longitude: enterprise.longitude,
          created_at: enterprise.created_at,
          license_status: healthByEmp[enterprise.id] ?? 'none',
        };
      });
    return list;
  }, [entPayload, licPayload]);

  const filteredEnterprises = useMemo(() => {
    return enterprises.filter((enterprise) => {
      if (filters.status && enterprise.is_active !== (filters.status === 'active')) return false;
      if (filters.type && enterprise.type !== filters.type) return false;
      if (filters.region && !String(enterprise.city || '').toLowerCase().includes(filters.region.toLowerCase()))
        return false;
      return true;
    });
  }, [enterprises, filters]);

  const handleEnterpriseDetail = useCallback(
    (enterprise) => {
      if (!enterprise?.id) return;
      navigate('/enterprises', { state: { openEnterpriseId: enterprise.id } });
    },
    [navigate]
  );

  return (
    <div className="card">
      <div className="card-header">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600">
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <h3 className="card-title">{title}</h3>
            <p className="card-subtitle">Todos os empreendimentos com coordenadas — cores pela validade da licença emitida</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Can permission={P.MONITORING_ACCESS}>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => setShowFilters((v) => !v)}
              aria-expanded={showFilters}
            >
              <Filter className="w-4 h-4 mr-1" />
              Filtros
            </button>
          </Can>
          <Can permission={P.MONITORING_ACCESS}>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => navigate('/map')}
            >
              <Eye className="w-4 h-4 mr-1" />
              Visualizar
            </button>
          </Can>
        </div>
      </div>
      {showFilters && (
        <div className="border-b border-gray-100 bg-slate-50/80 px-5 py-4 sm:px-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="form-group mb-0">
              <label className="form-label" htmlFor="dash-map-filter-status">
                Situação
              </label>
              <select
                id="dash-map-filter-status"
                className="form-select"
                value={filters.status}
                onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
              >
                <option value="">Todos</option>
                <option value="active">Ativo</option>
                <option value="inactive">Inativo</option>
              </select>
            </div>
            <div className="form-group mb-0">
              <label className="form-label" htmlFor="dash-map-filter-type">
                Tipo
              </label>
              <select
                id="dash-map-filter-type"
                className="form-select"
                value={filters.type}
                onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))}
              >
                <option value="">Todos</option>
                <option value="industrial">Industrial</option>
                <option value="commercial">Comercial</option>
                <option value="agricultural">Agrícola</option>
                <option value="residential">Residencial</option>
              </select>
            </div>
            <div className="form-group mb-0">
              <label className="form-label" htmlFor="dash-map-filter-region">
                Cidade / região
              </label>
              <input
                id="dash-map-filter-region"
                type="text"
                className="form-input"
                placeholder="Filtrar por cidade…"
                value={filters.region}
                onChange={(e) => setFilters((f) => ({ ...f, region: e.target.value }))}
              />
            </div>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Mostrando {filteredEnterprises.length} de {enterprises.length} empreendimento(s) com coordenadas.
          </p>
        </div>
      )}
      <div className="card-content p-0">
        {isLoading ? (
          <div className="flex h-80 items-center justify-center rounded-b-lg bg-gray-100">
            <div className="text-center">
              <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600"></div>
              <p className="text-sm text-gray-600">Carregando mapa...</p>
            </div>
          </div>
        ) : (
          <InteractiveMap
            enterprises={filteredEnterprises}
            onEnterpriseClick={handleEnterpriseDetail}
            center={[-35.7353, -9.6658]}
            zoom={11}
            height="380px"
            colorByLicenseStatus
          />
        )}
      </div>
    </div>
  );
};

export default MapCard;
